import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAppStore } from '@/store/useAppStore'
import { useNpsFeatures } from '@entities/nps'
import { SURVEYS, getSurvey } from '../lib/surveyConfig'
import { useNpsSurveyStore } from '../model/npsSurveyStore'
import { NpsModal } from './NpsModal'

/** Пауза перед показом: окно, выпрыгивающее в момент открытия экрана, читается
 *  как ошибка, а не как вопрос. */
const SHOW_DELAY_MS = 1500

/**
 * Решает, когда показать опрос.
 *
 * Список того, что можно оценить, приходит с бэка; где и на каком заходе
 * спрашивать — решает приложение. Одновременно показываем не больше одного
 * опроса и не больше одного за сессию.
 */
export const NpsSurveyHost = () => {
  const location = useLocation()
  const authStatus = useAppStore(s => s.authStatus)
  const { data } = useNpsFeatures(authStatus === 'success')
  const features = data?.features ?? []

  const visits = useNpsSurveyStore(s => s.visits)
  const countVisit = useNpsSurveyStore(s => s.countVisit)
  const askedInSession = useNpsSurveyStore(s => s.askedInSession)
  const markAsked = useNpsSurveyStore(s => s.markAsked)
  const manualSlug = useNpsSurveyStore(s => s.manualSlug)
  const closeSurvey = useNpsSurveyStore(s => s.closeSurvey)

  const [autoSlug, setAutoSlug] = useState<string | null>(null)

  // Заход считаем один раз на вход в экран, а не на каждый рендер.
  const countedPathRef = useRef<string | null>(null)
  useEffect(() => {
    if (countedPathRef.current === location.pathname) return
    countedPathRef.current = location.pathname

    SURVEYS.forEach(survey => {
      if (survey.trigger.type !== 'visits') return
      if (location.pathname !== survey.trigger.route) return
      countVisit(survey.slug)
    })
  }, [location.pathname, countVisit])

  // Уходим с экрана — снимаем окно, чтобы оно не всплыло уже на другом.
  useEffect(() => {
    setAutoSlug(null)
  }, [location.pathname])

  useEffect(() => {
    // Окно уже открыто: без этой проверки пометка «спросили» меняет состояние,
    // эффект перезапускается и тут же гасит только что показанный опрос.
    if (autoSlug) return
    if (features.length === 0) return

    const candidate = SURVEYS.find(survey => {
      if (survey.trigger.type !== 'visits') return false
      if (location.pathname !== survey.trigger.route) return false
      if (askedInSession.includes(survey.slug)) return false
      if (!features.some(feature => feature.slug === survey.slug)) return false
      return (visits[survey.slug] ?? 0) >= survey.trigger.minVisits
    })
    if (!candidate) return

    const timer = setTimeout(() => {
      setAutoSlug(candidate.slug)
      markAsked(candidate.slug)
    }, SHOW_DELAY_MS)
    return () => clearTimeout(timer)
  }, [autoSlug, location.pathname, features, visits, askedInSession, markAsked])

  const slug = manualSlug ?? autoSlug
  const survey = slug ? getSurvey(slug) : undefined
  if (!survey) return null

  const featureTitle =
    features.find(feature => feature.slug === survey.slug)?.title ?? ''

  return (
    <NpsModal
      survey={survey}
      featureTitle={featureTitle}
      onClose={() => {
        setAutoSlug(null)
        closeSurvey()
      }}
    />
  )
}
